// Shared between frontend and backend
import { TAILWIND_LOOKUP } from "@/lib/tailwind/properties";

export interface ClassType {
  tailwind: string;
  css: string;
}

export interface ClassTypeWithPropertyType extends ClassType {
  propertyType: PropertyType;
}

export enum PropertyType {
  WIDTH = "WIDTH",
  MIN_WIDTH = "MIN_WIDTH",
  MAX_WIDTH = "MAX_WIDTH",
  HEIGHT = "HEIGHT",
  MIN_HEIGHT = "MIN_HEIGHT",
  MAX_HEIGHT = "MAX_HEIGHT",
  ROTATE = "ROTATE",
  BORDER_RADIUS = "BORDER_RADIUS",
  BORDER_STYLE = "BORDER_STYLE",
  BORDER_COLOR = "BORDER_COLOR",
  BORDER_WIDTH = "BORDER_WIDTH",
  BORDER_TOP_COLOR = "BORDER_TOP_COLOR",
  BORDER_TOP_WIDTH = "BORDER_TOP_WIDTH",
  BORDER_BOTTOM_COLOR = "BORDER_BOTTOM_COLOR",
  BORDER_BOTTOM_WIDTH = "BORDER_BOTTOM_WIDTH",
  BORDER_LEFT_COLOR = "BORDER_LEFT_COLOR",
  BORDER_LEFT_WIDTH = "BORDER_LEFT_WIDTH",
  BORDER_RIGHT_COLOR = "BORDER_RIGHT_COLOR",
  BORDER_RIGHT_WIDTH = "BORDER_RIGHT_WIDTH",
  BORDER_TOP_LEFT_RADIUS = "BORDER_TOP_LEFT_RADIUS",
  BORDER_TOP_RIGHT_RADIUS = "BORDER_TOP_RIGHT_RADIUS",
  BORDER_BOTTOM_RIGHT_RADIUS = "BORDER_BOTTOM_RIGHT_RADIUS",
  BORDER_BOTTOM_LEFT_RADIUS = "BORDER_BOTTOM_LEFT_RADIUS",
  FONT_WEIGHT = "FONT_WEIGHT",
  FONT_SIZE = "FONT_SIZE",
  LINE_HEIGHT = "LINE_HEIGHT",
  LETTER_SPACING = "LETTER_SPACING",
  PADDING = "PADDING",
  PADDING_VERTICAL = "PADDING_VERTICAL",
  PADDING_HORIZONTAL = "PADDING_HORIZONTAL",
  PADDING_TOP = "PADDING_TOP",
  PADDING_BOTTOM = "PADDING_BOTTOM",
  PADDING_LEFT = "PADDING_LEFT",
  PADDING_RIGHT = "PADDING_RIGHT",
  MARGIN = "MARGIN",
  MARGIN_VERTICAL = "MARGIN_VERTICAL",
  MARGIN_HORIZONTAL = "MARGIN_HORIZONTAL",
  MARGIN_TOP = "MARGIN_TOP",
  MARGIN_BOTTOM = "MARGIN_BOTTOM",
  MARGIN_LEFT = "MARGIN_LEFT",
  MARGIN_RIGHT = "MARGIN_RIGHT",
  COLUMN_GAP = "COLUMN_GAP",
  ROW_GAP = "ROW_GAP",
  ANIMATION = "ANIMATION",
  BLUR = "BLUR",
  BOX_SHADOW = "BOX_SHADOW",
  OPACITY = "OPACITY",
  TEXT_COLOR = "TEXT_COLOR",
  BACKGROUND_COLOR = "BACKGROUND_COLOR",
  JUSTIFY_CONTENT = "JUSTIFY_CONTENT",
  ALIGN_ITEMS = "ALIGN_ITEMS",
  ALIGN_CONTENT = "ALIGN_CONTENT",
  FONT_FAMILY = "FONT_FAMILY",
  FONT_STYLE = "FONT_STYLE",
  TEXT_DECORATION_LINE = "TEXT_DECORATION_LINE",
  LEFT = "LEFT",
  TOP = "TOP",
  RIGHT = "RIGHT",
  BOTTOM = "BOTTOM",
  POSITION = "POSITION",
  BACKGROUND_IMAGE = "BACKGROUND_IMAGE",
  FROM_COLOR = "FROM_COLOR",
  TO_COLOR = "TO_COLOR",
  VIA_COLOR = "VIA_COLOR",
  FROM_POSITION = "FROM_POSITION",
  TO_POSITION = "TO_POSITION",
  VIA_POSITION = "VIA_POSITION",
  TEXT_ALIGN = "TEXT_ALIGN",
  DISPLAY = "DISPLAY",
  FLEX_DIRECTION = "FLEX_DIRECTION",
  FLEX_WRAP = "FLEX_WRAP",
  FLEX_GROW = "FLEX_GROW",
  FLEX_SHRINK = "FLEX_SHRINK",
}

export const tailwindFilterFunctions: {
  [key in PropertyType]: (prop: ClassType) => boolean;
} = {
  [PropertyType.WIDTH]: (prop) =>
    prop.css.startsWith("width:") || prop.tailwind.startsWith("w-"),
  [PropertyType.MIN_WIDTH]: (prop) =>
    prop.css.startsWith("min-width:") || prop.tailwind.startsWith("min-w-"),
  [PropertyType.MAX_WIDTH]: (prop) =>
    prop.css.startsWith("max-width:") || prop.tailwind.startsWith("max-w-"),
  [PropertyType.HEIGHT]: (prop) =>
    prop.css.startsWith("height:") || prop.tailwind.startsWith("h-"),
  [PropertyType.MIN_HEIGHT]: (prop) =>
    prop.css.startsWith("min-height:") || prop.tailwind.startsWith("min-h-"),
  [PropertyType.MAX_HEIGHT]: (prop) =>
    prop.css.startsWith("max-height:") || prop.tailwind.startsWith("max-h-"),
  [PropertyType.ROTATE]: (prop) => prop.tailwind.startsWith("rotate-"),
  [PropertyType.BORDER_RADIUS]: (prop) =>
    prop.css.startsWith("border-radius:") ||
    prop.tailwind.startsWith("rounded-"),
  [PropertyType.BORDER_TOP_LEFT_RADIUS]: (prop) =>
    prop.tailwind.startsWith("rounded-tl-"),
  [PropertyType.BORDER_TOP_RIGHT_RADIUS]: (prop) =>
    prop.tailwind.startsWith("rounded-tr-"),
  [PropertyType.BORDER_BOTTOM_RIGHT_RADIUS]: (prop) =>
    prop.tailwind.startsWith("rounded-br-"),
  [PropertyType.BORDER_BOTTOM_LEFT_RADIUS]: (prop) =>
    prop.tailwind.startsWith("rounded-bl-"),
  [PropertyType.BORDER_STYLE]: (prop) =>
    prop.css.startsWith("border-style:") && prop.tailwind.startsWith("border-"),
  [PropertyType.BORDER_COLOR]: (prop) =>
    prop.css.startsWith("border-color:") &&
    prop.tailwind.startsWith("border-") &&
    !prop.tailwind.includes("/"), // Filter only opacity variants
  [PropertyType.BORDER_WIDTH]: (prop) =>
    prop.css.startsWith("border-width:") && prop.tailwind.startsWith("border"),
  [PropertyType.BORDER_TOP_COLOR]: (prop) =>
    prop.css.startsWith("border-top-color:") &&
    prop.tailwind.startsWith("border-t-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.BORDER_TOP_WIDTH]: (prop) =>
    prop.css.startsWith("border-top-width:") &&
    prop.tailwind.startsWith("border-t"),
  [PropertyType.BORDER_LEFT_COLOR]: (prop) =>
    prop.css.startsWith("border-left-color:") &&
    prop.tailwind.startsWith("border-l-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.BORDER_LEFT_WIDTH]: (prop) =>
    prop.css.startsWith("border-left-width:") &&
    prop.tailwind.startsWith("border-l"),
  [PropertyType.BORDER_RIGHT_COLOR]: (prop) =>
    prop.css.startsWith("border-right-color:") &&
    prop.tailwind.startsWith("border-r-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.BORDER_RIGHT_WIDTH]: (prop) =>
    prop.css.startsWith("border-right-width:") &&
    prop.tailwind.startsWith("border-r"),
  [PropertyType.BORDER_BOTTOM_COLOR]: (prop) =>
    prop.css.startsWith("border-bottom-color:") &&
    prop.tailwind.startsWith("border-b-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.BORDER_BOTTOM_WIDTH]: (prop) =>
    prop.css.startsWith("border-bottom-width:") &&
    prop.tailwind.startsWith("border-b"),
  [PropertyType.FONT_WEIGHT]: (prop) => prop.css.startsWith("font-weight:"),
  [PropertyType.FONT_FAMILY]: (prop) => prop.css.startsWith("font-family:"),
  [PropertyType.FONT_STYLE]: (prop) => prop.css.startsWith("font-style:"),
  [PropertyType.TEXT_DECORATION_LINE]: (prop) =>
    ["underline", "overline", "line-through", "no-underline"].includes(
      prop.tailwind
    ),
  [PropertyType.FONT_SIZE]: (prop) => prop.css.startsWith("font-size:"),
  [PropertyType.LINE_HEIGHT]: (prop) =>
    prop.css.startsWith("line-height:") || prop.tailwind.startsWith("leading-"),
  [PropertyType.LETTER_SPACING]: (prop) =>
    prop.css.startsWith("letter-spacing:") ||
    prop.tailwind.startsWith("tracking-"),
  [PropertyType.PADDING]: (prop) =>
    prop.css.startsWith("padding:") || prop.tailwind.startsWith("p-"),
  [PropertyType.PADDING_VERTICAL]: (prop) => prop.tailwind.startsWith("py-"),
  [PropertyType.PADDING_HORIZONTAL]: (prop) => prop.tailwind.startsWith("px-"),
  [PropertyType.PADDING_TOP]: (prop) => prop.tailwind.startsWith("pt-"),
  [PropertyType.PADDING_BOTTOM]: (prop) => prop.tailwind.startsWith("pb-"),
  [PropertyType.PADDING_LEFT]: (prop) => prop.tailwind.startsWith("pl-"),
  [PropertyType.PADDING_RIGHT]: (prop) => prop.tailwind.startsWith("pr-"),
  [PropertyType.MARGIN]: (prop) =>
    prop.css.startsWith("margin:") || prop.tailwind.startsWith("m-"),
  [PropertyType.MARGIN_VERTICAL]: (prop) => prop.tailwind.startsWith("my-"),
  [PropertyType.MARGIN_HORIZONTAL]: (prop) => prop.tailwind.startsWith("mx-"),
  [PropertyType.MARGIN_TOP]: (prop) => prop.tailwind.startsWith("mt-"),
  [PropertyType.MARGIN_BOTTOM]: (prop) => prop.tailwind.startsWith("mb-"),
  [PropertyType.MARGIN_LEFT]: (prop) => prop.tailwind.startsWith("ml-"),
  [PropertyType.MARGIN_RIGHT]: (prop) => prop.tailwind.startsWith("mr-"),
  [PropertyType.COLUMN_GAP]: (prop) =>
    prop.css.startsWith("column-gap:") || prop.tailwind.startsWith("gap-x-"),
  [PropertyType.ROW_GAP]: (prop) =>
    prop.css.startsWith("row-gap:") || prop.tailwind.startsWith("gap-y-"),
  [PropertyType.ANIMATION]: (prop) => prop.tailwind.startsWith("animate-"),
  [PropertyType.BLUR]: (prop) => prop.tailwind.startsWith("blur"),
  [PropertyType.OPACITY]: (props) =>
    props.css.startsWith("opacity:") || props.tailwind.startsWith("opacity-"),
  [PropertyType.BACKGROUND_COLOR]: (prop) => {
    // More inclusive filter for debugging
    const hasBackgroundCss = prop.css.includes("background-color");
    const isBgClass = prop.tailwind.startsWith("bg-");
    const hasOpacity = prop.tailwind.includes("/");

    return hasBackgroundCss && isBgClass && !hasOpacity;
  },
  [PropertyType.JUSTIFY_CONTENT]: (prop) =>
    prop.css.startsWith("justify-content:"),

  [PropertyType.ALIGN_ITEMS]: (prop) => prop.css.startsWith("align-items:"),

  [PropertyType.ALIGN_CONTENT]: (prop) => prop.css.startsWith("align-content:"),

  [PropertyType.BOX_SHADOW]: (prop) => prop.css.includes("box-shadow:"),

  [PropertyType.TEXT_COLOR]: (prop) =>
    prop.css.startsWith("color:") &&
    prop.tailwind.startsWith("text-") &&
    !prop.tailwind.includes("/"), // Filter only opacity variants
  [PropertyType.LEFT]: (prop) =>
    prop.css.startsWith("left:") && prop.tailwind.startsWith("left-"),
  [PropertyType.TOP]: (prop) =>
    prop.css.startsWith("top:") && prop.tailwind.startsWith("top-"),
  [PropertyType.RIGHT]: (prop) =>
    prop.css.startsWith("right:") && prop.tailwind.startsWith("right-"),
  [PropertyType.BOTTOM]: (prop) =>
    prop.css.startsWith("bottom:") && prop.tailwind.startsWith("bottom-"),
  [PropertyType.POSITION]: (prop) =>
    prop.css.startsWith("position:") &&
    ["absolute", "relative", "fixed", "sticky", "static"].includes(
      prop.tailwind
    ),
  [PropertyType.BACKGROUND_IMAGE]: (prop) =>
    prop.css.startsWith("background-image:") &&
    ([
      "bg-none",
      "bg-gradient-to-t",
      "bg-gradient-to-tr",
      "bg-gradient-to-r",
      "bg-gradient-to-br",
      "bg-gradient-to-b",
      "bg-gradient-to-bl",
      "bg-gradient-to-l",
      "bg-gradient-to-tl",
    ].includes(prop.tailwind) ||
      prop.tailwind.startsWith("bg-[url(")),
  [PropertyType.FROM_COLOR]: (prop) =>
    prop.css.includes("--tw-gradient-from:") &&
    prop.tailwind.startsWith("from-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.VIA_COLOR]: (prop) =>
    prop.css.includes("--tw-gradient-stops:") &&
    prop.tailwind.startsWith("via-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.TO_COLOR]: (prop) =>
    prop.css.includes("--tw-gradient-to:") &&
    prop.tailwind.startsWith("to-") &&
    !prop.tailwind.includes("/"),
  [PropertyType.FROM_POSITION]: (prop) =>
    prop.tailwind.startsWith("from-") &&
    (prop.css.startsWith("--tw-gradient-from-position:") ||
      prop.tailwind.includes("[") ||
      !prop.css ||
      prop.css === ""),
  [PropertyType.TO_POSITION]: (prop) =>
    prop.tailwind.startsWith("to-") &&
    (prop.css.startsWith("--tw-gradient-to-position:") ||
      prop.tailwind.includes("[") ||
      !prop.css ||
      prop.css === ""),
  [PropertyType.VIA_POSITION]: (prop) =>
    prop.tailwind.startsWith("via-") &&
    (prop.css.startsWith("--tw-gradient-via-position:") ||
      prop.tailwind.includes("[") ||
      !prop.css ||
      prop.css === ""),
  [PropertyType.TEXT_ALIGN]: (prop) => prop.css.startsWith("text-align:"),
  [PropertyType.DISPLAY]: (prop) => prop.css.startsWith("display:"),
  [PropertyType.FLEX_DIRECTION]: (prop) =>
    prop.css.startsWith("flex-direction:"),
  [PropertyType.FLEX_WRAP]: (prop) => prop.css.startsWith("flex-wrap:"),
  [PropertyType.FLEX_GROW]: (prop) => prop.css.startsWith("flex-grow:"),
  [PropertyType.FLEX_SHRINK]: (prop) => prop.css.startsWith("flex-shrink:"),
};

export const checkArbitraryTailwindClass = (
  className: string,
  propertyType: PropertyType,
  tailwindClassMap: Record<string, ClassTypeWithPropertyType>
) => {
  const regexAndExtractors: {
    [propertyType: string]: [
      RegExp,
      (match: RegExpMatchArray) => string | null
    ];
  } = {
    [PropertyType.WIDTH]: [/w-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.MIN_WIDTH]: [/min-w-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.MAX_WIDTH]: [/max-w-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.HEIGHT]: [/h-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.MIN_HEIGHT]: [/min-h-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.MAX_HEIGHT]: [/max-h-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.ROTATE]: [/rotate-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.BORDER_RADIUS]: [
      /rounded-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.BORDER_TOP_LEFT_RADIUS]: [
      /rounded-tl-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.BORDER_TOP_RIGHT_RADIUS]: [
      /rounded-tr-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.BORDER_BOTTOM_RIGHT_RADIUS]: [
      /rounded-br-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.BORDER_BOTTOM_LEFT_RADIUS]: [
      /rounded-bl-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.FONT_WEIGHT]: [/font-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.LINE_HEIGHT]: [
      /leading-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.LETTER_SPACING]: [
      /tracking-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.PADDING]: [/p-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.PADDING_VERTICAL]: [
      /py-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.PADDING_HORIZONTAL]: [
      /px-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.PADDING_TOP]: [/pt-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.PADDING_BOTTOM]: [
      /pb-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.PADDING_LEFT]: [/pl-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.PADDING_RIGHT]: [/pr-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.MARGIN]: [/m-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.MARGIN_VERTICAL]: [
      /my-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.MARGIN_HORIZONTAL]: [
      /mx-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.MARGIN_TOP]: [/mt-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.MARGIN_BOTTOM]: [/mb-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.MARGIN_LEFT]: [/ml-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.MARGIN_RIGHT]: [/mr-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.COLUMN_GAP]: [/gap-x-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.ROW_GAP]: [/gap-y-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.ANIMATION]: [
      /animate-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.BLUR]: [/blur-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.OPACITY]: [/opacity-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    // Captures arbitrary background colors with brackets (e.g. bg-[#ff0000], bg-[blue])
    [PropertyType.BACKGROUND_COLOR]: [
      /bg-\[([^\[\]]+)\]$/,
      (match) => match[1],
    ],
    [PropertyType.JUSTIFY_CONTENT]: [
      /justify-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.ALIGN_ITEMS]: [
      /items-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.ALIGN_CONTENT]: [
      /content-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.BORDER_WIDTH]: [
      /border-\[?([^\[\]]*)\]?$/,
      (match) => {
        const value = match[1];
        if (/^\d+(px|em|rem|%)?$/.test(value)) return value;
        return null;
      },
    ],
    [PropertyType.BORDER_STYLE]: [
      /border-\[?([^\[\]]*)\]?$/,
      (match) => {
        const value = match[1];
        const borderStyles = [
          "solid",
          "dashed",
          "dotted",
          "double",
          "hidden",
          "none",
        ];
        if (borderStyles.includes(value)) return value;
        return null;
      },
    ],
    [PropertyType.BORDER_COLOR]: [
      /border-\[([^\[\]]+)\]$/,
      (match) => {
        const value = match[1];
        if (/^\d+(px|em|rem|%)?$/.test(value)) return null;
        const borderStyles = [
          "solid",
          "dashed",
          "dotted",
          "double",
          "groove",
          "ridge",
          "inset",
          "outset",
          "none",
          "hidden",
        ];
        if (borderStyles.includes(value)) return null;
        return value;
      },
    ],
    [PropertyType.FONT_SIZE]: [
      /text-\[?([^\[\]]*)\]?$/,
      (match) => {
        const value = match[1];

        // The first character must be a number
        if (isNaN(Number(value[0]))) {
          return null;
        }

        return value;
      },
    ],
    [PropertyType.TEXT_COLOR]: [
      /text-\[([^\[\]]+)\]$/,
      (match) => {
        const displayValue = match[1];
        if (
          ["left", "center", "right", "justify", "start", "end"].includes(
            displayValue
          )
        ) {
          return null;
        }

        const tailwindClass = tailwindClassMap[className];
        // If we have the tailwind class directly, check if it changes the font-size
        if (tailwindClass) {
          if (tailwindClass.propertyType === PropertyType.TEXT_COLOR) {
            return displayValue;
          } else {
            return null;
          }
        }

        // If value starts with a number, then it belongs to font-size and not text-color
        if (!isNaN(Number(displayValue[0]))) {
          return null;
        }

        return displayValue;
      },
    ],
    [PropertyType.BACKGROUND_IMAGE]: [
      /bg-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.LEFT]: [/left-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.TOP]: [/top-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.RIGHT]: [/right-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.BOTTOM]: [/bottom-\[?([^\[\]]*)\]?$/, (match) => match[1]],
    [PropertyType.POSITION]: [
      /position-\[?([^\[\]]*)\]?$/,
      (match) => match[1],
    ],
    [PropertyType.FROM_COLOR]: [/from-\[([^\[\]]+)\]$/, (match) => match[1]],
    [PropertyType.VIA_COLOR]: [/via-\[([^\[\]]+)\]$/, (match) => match[1]],
    [PropertyType.TO_COLOR]: [/to-\[([^\[\]]+)\]$/, (match) => match[1]],
    [PropertyType.FROM_POSITION]: [/from-\[([^\[\]]+)\]$/, (match) => match[1]],
    [PropertyType.VIA_POSITION]: [/via-\[([^\[\]]+)\]$/, (match) => match[1]],
    [PropertyType.TO_POSITION]: [/to-\[([^\[\]]+)\]$/, (match) => match[1]],
  };

  if (!regexAndExtractors[propertyType]) {
    return null;
  }

  const [regex, extractor] = regexAndExtractors[propertyType];
  if (!regex) {
    return null;
  }

  const match = className.match(regex);
  if (!match) {
    return null;
  }

  const propertyValue = extractor(match);
  if (!propertyValue) {
    return null;
  }

  return {
    propertyValue,
  };
};

export const getSupportedTailwindClasses = (
  classList: ClassType[]
): ClassTypeWithPropertyType[] => {
  const result: ClassTypeWithPropertyType[] = [];
  for (const classType of classList) {
    const propertyType = Object.keys(tailwindFilterFunctions).find((key) =>
      tailwindFilterFunctions[key as PropertyType](classType)
    );
    if (propertyType) {
      result.push({ ...classType, propertyType: propertyType as PropertyType });
    }
  }
  return result;
};

export const safeExtractValueFromCss = (tailwindClass: ClassType): string => {
  try {
    const firstCss = tailwindClass.css.split("\n")[0];
    let value: string = firstCss.split(":")[1];
    value = value.split(";")[0];

    value = value.trim();
    if (value.endsWith("!important")) {
      value = value.slice(0, -"!important".length);
    }

    value = value.trim();

    if (value.startsWith('"') && value.endsWith('"')) {
      value = value.slice(1, -1);
    }

    value = value.trim();

    return value;
  } catch (error) {
    return tailwindClass.tailwind;
  }
};

export function parseTailwindColorWithOpacity(
  className: string
): string | null {
  const match = className.match(
    /^(bg|border|text)-([a-z0-9-]+)(?:\/(\d{1,3}))?$/
  );
  if (!match) return null;
  const prefix = match[1];
  const color = match[2];
  const opacity = match[3] ? parseInt(match[3], 10) : 100;
  const baseClass = `${prefix}-${color}`;
  const css = TAILWIND_LOOKUP[baseClass];
  if (!css) return null;
  // Extract rgb from css string
  const rgbMatch = css.match(/rgb\(([^)]+)\)/);
  if (!rgbMatch) return null;
  const rgb = rgbMatch[1].split(/,| /).filter(Boolean).map(Number);
  const a = Math.round((opacity / 100) * 100) / 100;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`;
}

export const GRADIENT_PROPERTY_TYPES: PropertyType[] = [
  PropertyType.BACKGROUND_IMAGE,
  PropertyType.FROM_COLOR,
  PropertyType.TO_COLOR,
  PropertyType.VIA_COLOR,
  PropertyType.FROM_POSITION,
  PropertyType.VIA_POSITION,
  PropertyType.TO_POSITION,
];
